import { validIDToken } from './auth';
import { config } from './config';
import type {
  Conversion,
  DayRow,
  NewWalkRequest,
  PaceReport,
  Route,
  RouteRequest,
  SharedStatus,
  ShareLink,
  TargetTracking,
  Walk,
  WalkConfigRequest,
  WalkSummary,
} from './models';

export class APIError extends Error {}

/// Turn a failed response into something worth showing someone.
///
/// The API explains its refusals in plain English — "apply a route at least as
/// long as the distance covered so far". Wrapping that in a status code and a
/// JSON envelope buries the one part a person can act on, which is all they
/// see.
async function explain(response: Response): Promise<string> {
  if (response.status === 401) return 'Session expired. Sign in again.';

  const text = await response.text();
  try {
    const body = JSON.parse(text) as { error?: unknown };
    if (typeof body.error === 'string' && body.error !== '') return body.error;
  } catch {
    // Not JSON. Fall through to the status, which is at least true.
  }
  return `Server error ${response.status}`;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${config.apiBaseURL}${path}`, {
    method,
    headers: {
      Authorization: await validIDToken(),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) throw new APIError(await explain(response));
  return (await response.json()) as T;
}

export const api = {
  async walks(): Promise<Walk[]> {
    const { walks } = await send<{ walks: Walk[] }>('GET', '/walks');
    return walks;
  },

  /// The server has no timezone, so the browser says what "today" is.
  /// Without it the summary falls back to the last day that has a row, and a
  /// morning with no activity yet reads as a repeat of yesterday.
  summary: (walkId: string, asOf: string) =>
    send<WalkSummary>('GET', `/walks/${walkId}/summary?asOf=${asOf}`),

  async days(walkId: string): Promise<DayRow[]> {
    const { rows } = await send<{ rows: DayRow[] }>('GET', `/walks/${walkId}/days`);
    return rows;
  },

  pace: (walkId: string, date: string) =>
    send<PaceReport>('GET', `/walks/${walkId}/pace?date=${date}`),

  convert: (walkId: string, query: string) =>
    send<Conversion>('GET', `/walks/${walkId}/convert?${query}`),

  createWalk: (request: NewWalkRequest) => send<Walk>('POST', '/walks', request),

  async routes(): Promise<Route[]> {
    const { routes } = await send<{ routes: Route[] }>('GET', '/routes');
    return routes;
  },

  route: (routeId: string) => send<Route>('GET', `/routes/${routeId}`),

  async shares(walkId: string): Promise<ShareLink[]> {
    const { shares } = await send<{ shares: ShareLink[] }>('GET', `/walks/${walkId}/shares`);
    return shares;
  },

  /// `expiresInHours: null` never expires.
  createShare: (walkId: string, expiresInHours: number | null) =>
    send<ShareLink>('POST', `/walks/${walkId}/shares`, { expiresInHours }),

  revokeShare: (token: string) => send<{ revoked: string }>('DELETE', `/shares/${token}`),

  /// The one call made without a token, because the whole point is that the
  /// person opening it has no account. Deliberately not routed through `send`,
  /// which would try to mint an id token and fail before the request left.
  async sharedStatus(token: string): Promise<SharedStatus> {
    const response = await fetch(`${config.apiBaseURL}/shared/${encodeURIComponent(token)}`);
    if (!response.ok) throw new APIError(await explain(response));
    return (await response.json()) as SharedStatus;
  },

  createRoute: (request: RouteRequest) => send<Route>('POST', '/routes', request),

  updateRoute: (routeId: string, request: RouteRequest) =>
    send<Route>('PUT', `/routes/${routeId}`, request),

  deleteRoute: (routeId: string) =>
    send<{ deleted: string }>('DELETE', `/routes/${routeId}`),

  config: (walkId: string) => send<Walk>('GET', `/walks/${walkId}/config`),

  updateConfig: (walkId: string, request: WalkConfigRequest) =>
    send<Walk>('PUT', `/walks/${walkId}/config`, request),

  setTargetTracking: (walkId: string, tracking: TargetTracking) =>
    send<{ targetTracking: TargetTracking | null }>(
      'PUT',
      `/walks/${walkId}/target-tracking`,
      tracking,
    ),

  clearTargetTracking: (walkId: string) =>
    send<{ targetTracking: null }>('DELETE', `/walks/${walkId}/target-tracking`),

  /// Steps and miles are alternatives, not both: the server prefers steps when
  /// a day has both, so writing one replaces the other.
  setDaySteps: (walkId: string, date: string, steps: number) =>
    send<{ date: string }>('PUT', `/walks/${walkId}/days/${date}`, { steps }),

  setDayMiles: (walkId: string, date: string, dailyActualMiles: number) =>
    send<{ date: string }>('PUT', `/walks/${walkId}/days/${date}`, { dailyActualMiles }),
};
